import type { AnalyticsApiParams } from "@/react/api/analyticsApi";

export type ComparisonRole = "current" | "compare";

export const stableFiltersKey = (filters?: unknown) =>
  JSON.stringify(filters || {});

export const toNumber = (value: unknown): number =>
  typeof value === "number" ? value : Number(value || 0);

export const computeDeltaPercent = (
  curr: number,
  prev: number,
): number | undefined => {
  if (!Number.isFinite(curr) || !Number.isFinite(prev)) return undefined;
  if (prev === 0) return undefined;
  const deltaAbs = curr - prev;
  return (deltaAbs / Math.abs(prev)) * 100;
};

export const buildComparisonKey = (
  metricKey: string,
  role: ComparisonRole,
  params: AnalyticsApiParams,
) => [
  "analyticsV4",
  ...metricKey.split("."),
  role,
  params.start_date,
  params.end_date,
  params.date_grouping,
  stableFiltersKey(params.filters),
];

export interface SeriesPoint {
  date: string;
  value: number;
}

export const alignSeriesDates = (
  current: SeriesPoint[],
  compare: SeriesPoint[],
) => {
  const currMap = new Map<string, number>(
    current.map(p => [p.date, toNumber(p.value)]),
  );
  const prevMap = new Map<string, number>(
    compare.map(p => [p.date, toNumber(p.value)]),
  );
  const dates = Array.from(
    new Set<string>([...current.map(p => p.date), ...compare.map(p => p.date)]),
  ).sort((a, b) => a.localeCompare(b));
  const currentAligned = dates.map(date => ({
    date,
    value: currMap.get(date) ?? 0,
  }));
  const compareAligned = dates.map(date => ({
    date,
    value: prevMap.get(date) ?? 0,
  }));
  return { dates, currentAligned, compareAligned };
};
